import { FC } from 'react'
import { Chip, Tooltip } from '@mui/material'
import type { MotivoRecusaArquivoNf, NotaFiscalComissao } from '@/types/comissao'

/**
 * Onda 5 (AN-11 §3.3–3.5): o arquivo anexado à NF só vale depois que oportunidades confirma,
 * pelo vínculo publicado por documentos, que ele foi enviado PARA ESTA NOTA, no tenant dela,
 * pelo consultor dono (ou por quem anexou). A situação vem pronta do backend
 * (`documentoVerificacao`); aqui só se rotula e se explica o bloqueio.
 */

/** Texto único para quando o arquivo não foi enviado para esta nota (sem dizer de quem/de que empresa é). */
const NAO_ENVIADO_PARA_ESTA_NOTA = 'o arquivo não foi enviado para esta nota'

/**
 * Motivo da recusa em texto neutro — nunca diz de quem nem de que empresa é o arquivo. O consultor
 * só recebe `ARQUIVO_INVALIDO`/`REMOVIDO`; os demais chegam ao back-office, e ainda assim a tela
 * não nomeia dono nem empresa (o detalhe fica na auditoria).
 */
export const TEXTO_MOTIVO_RECUSA: Record<MotivoRecusaArquivoNf, string> = {
  TIPO: 'o arquivo não foi enviado como arquivo de nota fiscal',
  OUTRA_NOTA: 'o arquivo foi enviado para outra nota',
  OUTRO_TENANT: NAO_ENVIADO_PARA_ESTA_NOTA,
  OUTRO_DONO: NAO_ENVIADO_PARA_ESTA_NOTA,
  ARQUIVO_INVALIDO: NAO_ENVIADO_PARA_ESTA_NOTA,
  REMOVIDO: 'o arquivo foi removido',
}

/** O código é um motivo conhecido pela tela? */
export const ehMotivoConhecido = (m: unknown): m is MotivoRecusaArquivoNf =>
  typeof m === 'string' && Object.prototype.hasOwnProperty.call(TEXTO_MOTIVO_RECUSA, m)

/**
 * Motivo da recusa: o texto local do código (neutro) quando o código é conhecido; senão o texto
 * pronto do backend (`documentoVerificacaoDescricao`); senão um genérico. O texto do backend NÃO
 * vale mais que o local: para o back-office ele traz o detalhe (ex.: "não pertence a esta empresa").
 */
export const textoMotivoRecusa = (
  n: Pick<NotaFiscalComissao, 'documentoVerificacaoMotivo' | 'documentoVerificacaoDescricao'>
): string =>
  (ehMotivoConhecido(n.documentoVerificacaoMotivo) && TEXTO_MOTIVO_RECUSA[n.documentoVerificacaoMotivo]) ||
  n.documentoVerificacaoDescricao?.trim() ||
  'o arquivo não pôde ser confirmado'

export const arquivoRecusado = (n: NotaFiscalComissao): boolean =>
  !!n.documentoId && n.documentoVerificacao === 'RECUSADO'

/**
 * Por que Conferir/Aprovar fiscal não podem seguir por causa do arquivo — `null` quando o
 * arquivo está VERIFICADO/LEGADO (ou o backend é anterior à Onda 5 e não manda o campo: aí o
 * servidor continua sendo quem decide). Espelha o gate 409 do servidor só para avisar antes.
 */
export const motivoArquivoNaoLiberado = (n: NotaFiscalComissao): string | null => {
  if (!n.documentoId) return null
  switch (n.documentoVerificacao) {
    case 'PENDENTE':
      return 'O arquivo da nota ainda está em verificação — aguarde instantes.'
    case 'RECUSADO':
      return `Arquivo recusado: ${textoMotivoRecusa(n)}. É preciso anexar o arquivo novamente.`
    default:
      return null
  }
}

/** Selo da coluna "Arquivo" do Financeiro (consultor e back-office). */
export const SeloArquivoNf: FC<{ nota: NotaFiscalComissao }> = ({ nota }) => {
  if (!nota.documentoId) {
    return <Chip size="small" label="Sem arquivo" variant="outlined" color="warning" />
  }
  if (nota.documentoVerificacao === 'PENDENTE') {
    return (
      <Tooltip title="A Originei está confirmando o arquivo anexado. Leva só alguns instantes.">
        <Chip size="small" label="Arquivo em verificação" variant="outlined" color="info" />
      </Tooltip>
    )
  }
  if (nota.documentoVerificacao === 'RECUSADO') {
    const motivo = textoMotivoRecusa(nota)
    return (
      <Tooltip title={`Motivo: ${motivo}.`}>
        <Chip
          size="small"
          label="Arquivo recusado — anexe novamente"
          color="error"
          aria-label={`Arquivo recusado — anexe novamente. Motivo: ${motivo}.`}
        />
      </Tooltip>
    )
  }
  return <Chip size="small" label="Anexado" variant="outlined" color="success" />
}
